/**
 * Visitor theme preference — system / light / dark — with
 * localStorage persistence and live OS-preference tracking when set
 * to "system."
 *
 * Replaces the prior `useOsDarkMode` which only read OS preference.
 * That hook is kept as a thin wrapper around this one for back-
 * compat, but new call sites should use `useThemePreference()` and
 * surface the `mode` + `setMode` to the user.
 *
 * The hook owns the canonical writes:
 *   - `<html>.dark` class (Tailwind v4 dark variant)
 *   - `<html>[data-theme="dark"]` attribute (story-render.js SVG color
 *     selector)
 *   - `mp:theme-change` CustomEvent (story.tsx / pilot-engine.js
 *     observers re-fire on flip)
 *
 * The store is module-level (single source for the whole app) so a
 * <ThemeToggle> in the navbar and a `useOsDarkMode()` mount in
 * PilotLandingPage both observe the same value.
 */

import {useCallback, useSyncExternalStore} from 'react';
import {MP_THEME_CHANGE_EVENT} from '../lib/pilot/events';

export type ThemeMode = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'mp-theme-mode';
const DARK_CLASS = 'dark';
const DARK_ATTR = 'data-theme';
const DARK_MEDIA = '(prefers-color-scheme: dark)';

function readStored(): ThemeMode {
  // Default is 'light' (was 'system'). The /pilot site is designed
  // primarily for light theme; admin-only visitors who want to flip
  // can do so via the gated ThemeToggle in the navbar. A returning
  // visitor who picked 'dark' or 'system' previously keeps that
  // choice — only first-time visitors land on light.
  if (typeof window === 'undefined') return 'light';
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* private mode */
  }
  return 'light';
}

function osPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  try {
    return window.matchMedia(DARK_MEDIA).matches;
  } catch {
    return false;
  }
}

function resolve(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') return osPrefersDark() ? 'dark' : 'light';
  return mode;
}

let _mode: ThemeMode = readStored();
const _listeners = new Set<() => void>();

function subscribe(cb: () => void): () => void {
  _listeners.add(cb);
  return () => {
    _listeners.delete(cb);
  };
}

function snapshot(): ThemeMode {
  return _mode;
}

function notify(): void {
  _listeners.forEach((cb) => {
    try {
      cb();
    } catch {
      /* one bad listener shouldn't poison the rest */
    }
  });
}

function applyDom(mode: ThemeMode): void {
  if (typeof document === 'undefined') return;
  const resolved = resolve(mode);
  const root = document.documentElement;
  const isDark = resolved === 'dark';
  root.classList.toggle(DARK_CLASS, isDark);
  if (isDark) root.setAttribute(DARK_ATTR, 'dark');
  else root.removeAttribute(DARK_ATTR);
  try {
    window.dispatchEvent(
      new CustomEvent(MP_THEME_CHANGE_EVENT, {detail: {name: resolved}}),
    );
  } catch {
    /* CustomEvent unsupported */
  }
}

let _mqlBound = false;
function ensureMediaSync(): void {
  if (_mqlBound || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return;
  }
  _mqlBound = true;
  const mql = window.matchMedia(DARK_MEDIA);
  const onChange = () => {
    // Only re-apply if the visitor is in "system" mode — explicit
    // light/dark choices should ignore OS preference changes.
    if (_mode === 'system') {
      applyDom('system');
    }
  };
  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', onChange);
  } else if (typeof (mql as MediaQueryList & {addListener?: (fn: () => void) => void}).addListener === 'function') {
    (mql as MediaQueryList & {addListener: (fn: () => void) => void}).addListener(onChange);
  }
}

function setMode(next: ThemeMode): void {
  if (next !== 'system' && next !== 'light' && next !== 'dark') return;
  _mode = next;
  try {
    if (next === 'system') {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
  } catch {
    /* private mode */
  }
  applyDom(next);
  notify();
}

/**
 * Subscribe a component to the visitor's theme preference. The hook
 * also applies the current resolved theme to `<html>` on mount and
 * wires the OS media-query listener (once per app session) so
 * `system` mode tracks changes live.
 */
export function useThemePreference(): {
  mode: ThemeMode;
  resolved: 'light' | 'dark';
  setMode: (next: ThemeMode) => void;
} {
  const mode = useSyncExternalStore(subscribe, snapshot, snapshot);
  // Apply DOM + bind media listener on first render. The DOM write
  // is idempotent so repeat mounts (e.g. PilotLandingPage + a
  // ThemeToggle both calling the hook) don't fight.
  if (typeof window !== 'undefined') {
    ensureMediaSync();
    applyDom(mode);
  }
  const setModeCb = useCallback((next: ThemeMode) => setMode(next), []);
  return {mode, resolved: resolve(mode), setMode: setModeCb};
}

// ---------------------------------------------------------------------------
// Test seam — production code should never reach for this. It exists so a
// future unit test can reset the module's hidden state between cases.
// ---------------------------------------------------------------------------

export const __testing = {
  reset(): void {
    _mode = 'system';
    _mqlBound = false;
    _listeners.clear();
  },
};
